import { ArrowRightLeft, LogOut, Smartphone } from "lucide-react";
import { useNavigate } from "react-router";
import { useLogout, useSession, useSwitchOrganisation } from "@/shared/auth/session";
import { ROUTES } from "@/shared/constants/routes";
import { Brand } from "@/shared/layout/Brand";
import { Button } from "@/shared/ui/button";
import { Icon } from "@/shared/ui/icon";
import { MOBILE_APP_LINKS } from "@/shared/constants/mobileApp";

/**
 * What an **employee-only** membership sees in the web portal (C1-15, ADR-0023 decision 4). Employee
 * self-service is mobile-only in v1 (M19 plan §11 item 4), and the `employee` preset holds no
 * permission, so instead of a shell full of refusals they get one page: where to go, how to get the
 * app, the other organisations they can open here, and a way out.
 */
export function EmployeeOnlyPage() {
  const session = useSession();
  const navigate = useNavigate();
  const logout = useLogout(() => void navigate(ROUTES.login, { replace: true }));
  const switchOrganisation = useSwitchOrganisation();
  const others = session.memberships.filter((m) => m.tenantId !== session.tenant.id && m.status === "active");

  return (
    <main className="flex min-h-dvh items-center justify-center bg-bg p-6">
      <div className="flex w-full max-w-md flex-col gap-5 rounded-lg border border-border bg-surface p-6">
        <Brand size="md" />
        <div className="flex items-start gap-3">
          <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-md border border-border bg-bg text-fg-muted">
            <Icon icon={Smartphone} size={20} />
          </span>
          <div className="min-w-0">
            <h1 className="text-title font-semibold text-fg">Use the NexBorg Access mobile app</h1>
            <p className="mt-1 text-cell text-fg-muted">
              {session.tenant.name} uses the NexBorg Access mobile app for attendance. Sign in there with this
              mobile number to punch in and out, see your attendance and apply for leave.
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button asChild variant="primary">
            <a href={MOBILE_APP_LINKS.android} target="_blank" rel="noopener noreferrer">
              Get it on Google Play
            </a>
          </Button>
          <Button asChild variant="secondary">
            <a href={MOBILE_APP_LINKS.ios} target="_blank" rel="noopener noreferrer">
              Download on the App Store
            </a>
          </Button>
        </div>
        {others.length > 0 ? (
          <div className="flex flex-col gap-2 border-t border-border pt-4">
            <p className="text-cell font-medium text-fg">Your other organisations</p>
            {others.map((m) => (
              <Button
                key={m.membershipId}
                variant="secondary"
                className="justify-between"
                loading={
                  switchOrganisation.isPending &&
                  switchOrganisation.variables?.membershipId === m.membershipId
                }
                onClick={() =>
                  switchOrganisation.mutate(
                    { membershipId: m.membershipId },
                    { onSuccess: () => void navigate(ROUTES.overview, { replace: true }) },
                  )
                }
              >
                <span className="truncate">
                  {m.tenantName} · {m.roleName}
                </span>
                <Icon icon={ArrowRightLeft} size={16} />
              </Button>
            ))}
          </div>
        ) : null}
        <Button variant="ghost" loading={logout.isPending} onClick={() => logout.mutate()}>
          <Icon icon={LogOut} size={16} />
          Sign out
        </Button>
      </div>
    </main>
  );
}
